/* HB 2026-10-09 (Vic, page weight): videos load when they are about to be seen, in the rendition their stage needs.
 *
 * Every Shopify-hosted video in the theme is written by snippets/hb-video.liquid: a <video> with a poster, no
 * <source>, the file Liquid knows in data-hb-src ("url width height") and the HLS playlist in data-hb-hls. Liquid
 * only sees the biggest mp4 (1080p); the renditions Shopify also made (480p, 720p) are listed in the playlist, a
 * 500-byte text file this loader reads once per video and turns into the mp4 addresses (the names follow one
 * pattern: "<id>.HD-720p.4.5Mbps.hls.<n>_.m3u8" is the playlist of "<id>.HD-720p-4.5Mbps-<n>.mp4"). Nothing
 * downloads until the loader gives the element a source, which happens when the video comes within a screen of
 * the viewport (IntersectionObserver). So:
 *   - a copy that is not shown (the hidden phone or desktop layout of the product gallery, the zoom dialog until it
 *     opens, a homepage block hidden on this device, a slide out of view) never loads;
 *   - the rendition follows the stage's width: up to 200 px (a tile, a phone card) 480p, up to 450 px (a phone's
 *     full width: the product gallery, a homepage block) 720p, wider (desktop galleries and blocks) 1080p; the
 *     known 1080p file is always appended as the fallback source, so a rendition that is missing falls through;
 *   - a video added later (recently viewed cards, a swapped card, a re-rendered section, the zoom dialog's clone)
 *     is picked up by a MutationObserver;
 *   - autoplay videos (the "autoplay" attribute) start when they show and pause while out of view.
 * Data saver or a 2G connection: always the smallest rendition.
 *
 * 2026-10-09 evening (Vic's phone test, 20% battery, Low Power Mode, the Silks collection crashing by page 3):
 *   - on phones at most two videos are alive at a time (desktop: four); the ones nearest the viewport win, the
 *     others give their sources back (pause, remove the sources, load()) so the decoder memory is freed, and get
 *     them again when they come near. A video is "near" within half a screen on phones, a full screen on desktop;
 *   - iOS in Low Power Mode refuses autoplay until the first touch: a refused play() is remembered and the first
 *     touch, tap or key press (a scroll counts) starts every autoplay video that is on screen. Until then iOS shows
 *     its own play glyph on the poster frame, kept on purpose (Vic, 21:30 CPH): the shopper sees it is a video.
 *
 * 2026-10-10 (Vic: a quick add, then a fast scroll down a collection, and Safari reloaded the page): the pictures of
 * the product cards are given back too. A card holds up to 14 pictures (seven slides, two of them in a model and a
 * product view, seven colour dots), and the infinite scroll never removes a card, so after a flick through 170 cards
 * the page held 1,400 loaded pictures at 380 px, which is what WebKit's memory limit answers with a reload. Now a
 * card more than two screens from the viewport (phones; three on desktop) hands its pictures' src and srcset to
 * data attributes and gets them back the moment it comes within reach; the browser's lazy loading does the rest, and
 * a picture that was shown once comes back from the cache. The same IntersectionObserver idea as the videos.
 *
 * 2026-10-10, later (Vic: the reload again, flicking while a note was up): the pictures behind a card's first slide
 * (the second view and the lookbook shots, slides 2 and up, four to six per card) are kept back too, from the start
 * and after every return, until she reaches for them: a touch or a press on the card's slideshow or its arrows, a
 * swatch, a swipe (the slideshow scrolling) or, with a mouse, the pointer over the card. A card near the viewport
 * then holds one picture (its first slide, plus the hover view on desktop) instead of up to eight, so a flick through
 * the collection costs a quarter of the memory it did.
 *
 * 2026-10-10, round 28 (the reload measured): the pictures were not the whole story. In this theme every change of
 * an attribute that appears inside a :has() argument (aria-hidden on a slide, open on a dialog, hidden, the header's
 * sticky state) restyles the entire document, because base.css and the compiled styles anchor :has() on body and on
 * the section; with 176 cards (22,000 elements) one such change cost 130 to 270 ms in Chrome on a Mac, and a page of
 * cards added by the infinite scroll (its slideshows write aria-hidden and disabled while they set up) cost about a
 * second, growing with every page. On a phone that is a main thread hung for the length of a flick, which Safari
 * answers by reloading the page. Now a card of the collection grid that is more than two screens away (three on
 * desktop) is not rendered at all: content-visibility: hidden with its measured height kept as the box's intrinsic
 * block size, so nothing moves; its style, layout, paint and compositing are skipped, and a restyle of the document
 * only touches the cards in reach (about 45 on a phone). A page the infinite scroll adds beyond the margin is skipped
 * from the moment it lands, before its slideshows do their layout reads. Pictures are still given back as before
 * (browsers without content-visibility keep that much). The cards come back the moment they are within reach.
 */
(function () {
  'use strict';
  if (typeof window === 'undefined' || !document.querySelector) return;

  var SEL = 'video[data-hb-src]';
  var conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection || null;
  var playlists = {};
  var PHONE = (window.matchMedia && window.matchMedia('(max-width: 749px)').matches) || (navigator.maxTouchPoints > 1 && window.innerWidth < 1000);
  var MAX_ALIVE = PHONE ? 2 : 4;
  var NEAR = PHONE ? '50% 0px 50% 0px' : '100% 0px 100% 0px';
  var alive = []; // videos that currently hold a source, nearest first after each reconcile
  var wanted = []; // videos within the near margin, as the observer reports them
  var blocked = false; // autoplay refused (Low Power Mode): waiting for a user gesture

  function parseList(raw) {
    var list = [];
    String(raw || '').split(',').forEach(function (part) {
      var bits = part.trim().split(/\s+/);
      if (bits.length < 3) return;
      var w = parseInt(bits[1], 10);
      var h = parseInt(bits[2], 10);
      if (!bits[0] || !(w > 0)) return;
      list.push({ url: bits[0], w: w, h: h });
    });
    return list;
  }

  // The playlist's variants, as mp4 addresses next to the playlist.
  function fromPlaylist(text, hlsUrl) {
    var out = [];
    var dir = hlsUrl.substring(0, hlsUrl.lastIndexOf('/') + 1);
    var lines = String(text || '').split(/\r?\n/);
    var pending = null;
    for (var i = 0; i < lines.length; i++) {
      var line = lines[i].trim();
      if (!line) continue;
      if (line.indexOf('#EXT-X-STREAM-INF') === 0) {
        var m = /RESOLUTION=(\d+)x(\d+)/.exec(line);
        pending = m ? { w: parseInt(m[1], 10), h: parseInt(m[2], 10) } : null;
        continue;
      }
      if (line.charAt(0) === '#') continue;
      if (pending) {
        var name = /^(.+?)\.([A-Za-z]+-\d+p)\.([\d.]+Mbps)\.hls\.(\d+)_?\.m3u8$/.exec(line.replace(/\?.*$/, ''));
        if (name && pending.w > 0) out.push({ url: dir + name[1] + '.' + name[2] + '-' + name[3] + '-' + name[4] + '.mp4', w: pending.w, h: pending.h });
        pending = null;
      }
    }
    return out;
  }

  function renditions(video) {
    var base = parseList(video.getAttribute('data-hb-src'));
    var hls = video.getAttribute('data-hb-hls');
    if (!hls || !window.fetch) return Promise.resolve(base);
    if (!playlists[hls]) {
      playlists[hls] = fetch(hls, { credentials: 'omit' })
        .then(function (r) { return r.ok ? r.text() : ''; })
        .then(function (text) {
          var found = fromPlaylist(text, hls);
          // the file Liquid knows stays in the list (it is the fallback); the playlist adds the smaller ones
          // A phone recording is often stored on its side and turned by the player: Liquid may say 1080x1920 where
          // the playlist says 1920x1080 for the same file, so a rendition is the same when its two sides match.
          var all = base.slice();
          found.forEach(function (f) {
            var dup = false;
            for (var i = 0; i < all.length; i++) {
              if (Math.max(all[i].w, all[i].h) === Math.max(f.w, f.h) && Math.min(all[i].w, all[i].h) === Math.min(f.w, f.h)) dup = true;
            }
            if (!dup) all.push(f);
          });
          return all;
        })
        .catch(function () { return base; });
    }
    return playlists[hls];
  }

  function stageWidth(video) {
    var w = video.getBoundingClientRect().width;
    if (!w && video.parentElement) w = video.parentElement.getBoundingClientRect().width;
    if (!w) w = Math.min(window.innerWidth || 0, 750);
    return w;
  }

  // Three tiers by the stage's width in CSS pixels: a tile or card up to 200 px plays 480p, anything up to 450 px
  // (a phone's full width) 720p, wider stages 1080p. A rendition is known by the "p" number in its file name
  // (SD-480p, HD-720p, HD-1080p), the same whichever way the recording is stored.
  function pOf(r) {
    var m = /[-.](\d{3,4})p[-.]/.exec(r.url || '');
    return m ? parseInt(m[1], 10) : Math.min(r.w, r.h);
  }

  function tier(video) {
    var thrifty = conn && (conn.saveData || /(^|-)2g$/.test(String(conn.effectiveType || '')));
    if (thrifty) return 480;
    var w = stageWidth(video);
    if (w <= 200) return 480;
    if (w <= 450) return 720;
    return 1080;
  }

  function choose(video, list) {
    if (!list.length) return null;
    var want = tier(video);
    var sorted = list.map(function (r) { return { url: r.url, w: r.w, h: r.h, p: pOf(r) }; });
    sorted.sort(function (a, b) { return a.p - b.p; });
    for (var i = 0; i < sorted.length; i++) {
      if (sorted[i].p >= want) return sorted[i];
    }
    return sorted[sorted.length - 1];
  }

  function addSource(video, url) {
    var source = document.createElement('source');
    source.src = url;
    source.type = 'video/mp4';
    source.setAttribute('data-hb', '1');
    video.appendChild(source);
  }

  function attach(video) {
    if (video.__hbSrc || video.__hbBusy) return;
    video.__hbBusy = true;
    renditions(video).then(function (list) {
      video.__hbBusy = false;
      if (video.__hbSrc || !video.isConnected) return;
      if (alive.indexOf(video) === -1) alive.push(video);
      var r = choose(video, list);
      if (!r) return;
      var base = parseList(video.getAttribute('data-hb-src'));
      var fallback = base.length ? base[base.length - 1].url : null;
      video.__hbSrc = r.url;
      video.__hbFallback = fallback && fallback !== r.url ? fallback : null;
      var lazyPoster = video.getAttribute('data-hb-poster');
      if (lazyPoster && !video.getAttribute('poster')) video.setAttribute('poster', lazyPoster);
      addSource(video, r.url);
      if (video.__hbFallback) addSource(video, video.__hbFallback);
      if (video.hasAttribute('autoplay')) video.muted = true;
      if (video.getAttribute('preload') === 'none') video.setAttribute('preload', 'metadata');
      try { video.load(); } catch (e) { /* no-op */ }
      play(video);
      if (video.__hbWantsPlay) { video.__hbWantsPlay = false; var p = video.play(); if (p && typeof p.catch === 'function') p.catch(function () { /* no-op */ }); }
      scheduleReconcile();
    });
  }

  function play(video) {
    if (!video.hasAttribute('autoplay') || !video.__hbSrc) return;
    if (!video.paused) return;
    video.muted = true;
    var p = video.play();
    if (p && typeof p.catch === 'function') p.catch(function (err) {
      // NotAllowedError: autoplay refused (Low Power Mode, or no gesture yet); a gesture retries it
      if (err && err.name === 'NotAllowedError') blocked = true;
    });
  }

  function pause(video) {
    if (!video.hasAttribute('autoplay')) return;
    if (!video.paused) {
      try { video.pause(); } catch (e) { /* no-op */ }
    }
  }

  // Gives the sources back so the decoder is released; the video is attached again when it comes near.
  function detach(video) {
    var i = alive.indexOf(video);
    if (i !== -1) alive.splice(i, 1);
    if (!video.__hbSrc) return;
    pause(video);
    var sources = video.querySelectorAll('source[data-hb]');
    for (var k = 0; k < sources.length; k++) sources[k].parentNode.removeChild(sources[k]);
    video.__hbSrc = null;
    video.__hbFallback = null;
    video.removeAttribute('src');
    try { video.load(); } catch (e) { /* no-op */ }
  }

  // Distance from the viewport's centre, in viewport heights; 0 while on screen.
  function distance(video) {
    var r = video.getBoundingClientRect();
    var vh = window.innerHeight || 1;
    if (r.bottom >= 0 && r.top <= vh) return 0;
    return (r.top > vh ? r.top - vh : -r.bottom) / vh;
  }

  // The nearest wanted videos get the MAX_ALIVE places; everything else alive gives its sources back.
  var reconcileTimer = 0;
  function reconcile() {
    reconcileTimer = 0;
    var i;
    for (i = wanted.length - 1; i >= 0; i--) if (!wanted[i].isConnected) wanted.splice(i, 1);
    var ranked = wanted.slice().sort(function (a, b) { return distance(a) - distance(b); });
    var keep = ranked.slice(0, MAX_ALIVE);
    for (i = alive.length - 1; i >= 0; i--) {
      if (keep.indexOf(alive[i]) === -1 && distance(alive[i]) > 0) detach(alive[i]);
    }
    for (i = 0; i < keep.length; i++) {
      if (keep[i].__hbSrc || keep[i].__hbBusy) continue;
      if (alive.length >= MAX_ALIVE) break;
      attach(keep[i]);
    }
    for (i = 0; i < alive.length; i++) {
      if (distance(alive[i]) === 0) play(alive[i]);
      else pause(alive[i]);
    }
  }
  function scheduleReconcile() {
    if (reconcileTimer) return;
    reconcileTimer = setTimeout(reconcile, 120);
  }

  var io = null;
  if ('IntersectionObserver' in window) {
    io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var video = entry.target;
        var at = wanted.indexOf(video);
        if (entry.isIntersecting) {
          if (at === -1) wanted.push(video);
        } else {
          if (at !== -1) wanted.splice(at, 1);
          if (video.__hbSrc) pause(video);
        }
      });
      scheduleReconcile();
    }, { rootMargin: NEAR, threshold: 0 });
    window.addEventListener('scroll', scheduleReconcile, { passive: true });
    window.addEventListener('resize', scheduleReconcile, { passive: true });
  }

  // Low Power Mode (iOS) refuses autoplay until a user gesture: the first touch starts what is on screen.
  function onGesture() {
    // every touch or key press, not only the first: in Low Power Mode each video may need its own gesture.
    // Any autoplay video on screen, the loader's or an old-style one with its own <source>.
    blocked = false;
    var all = document.querySelectorAll('video[autoplay]:not([controls])');
    for (var i = 0; i < all.length; i++) {
      var v = all[i];
      if (!v.paused || distance(v) !== 0) continue;
      if (!v.__hbSrc && !v.currentSrc && !v.querySelector('source')) continue;
      v.muted = true;
      var p = v.play();
      if (p && typeof p.catch === 'function') p.catch(function () { blocked = true; });
    }
  }
  ['touchstart', 'touchend', 'pointerdown', 'mousedown', 'keydown'].forEach(function (type) {
    document.addEventListener(type, onGesture, { passive: true, capture: true });
  });

  function watch(video) {
    if (video.__hbWatched) return;
    video.__hbWatched = true;
    // A poster held back for a device-specific copy (data-hb-poster) is set at once when the copy is laid out on
    // this device, so a block on the first screen shows its frame without waiting for the observer.
    var held = video.getAttribute('data-hb-poster');
    if (held && !video.getAttribute('poster') && video.getBoundingClientRect().width > 0) video.setAttribute('poster', held);
    // A tap on the video (or a play from the theme's own buttons) always gets a source, whatever the observer saw.
    video.addEventListener('pointerdown', function () { if (wanted.indexOf(video) === -1) wanted.push(video); attach(video); }, true);
    video.addEventListener('play', function () {
      if (video.__hbSrc) return;
      // The theme's own scripts call play() on every card video at load; only one that is near the viewport
      // gets a source for it (the cap would be meaningless otherwise). A far one is picked up by the observer
      // when it comes near.
      if (distance(video) > (PHONE ? 0.5 : 1)) {
        try { video.pause(); } catch (e) { /* no-op */ }
        return;
      }
      video.__hbWantsPlay = true; // the play she asked for is repeated once the source is in
      if (wanted.indexOf(video) === -1) wanted.push(video);
      attach(video);
    }, true);
    if (io) io.observe(video);
    else attach(video);
  }

  /* ---------- the cards' pictures: given back when far, taken back when near (2026-10-10) ---------- */
  var CARD_SEL = 'product-card';
  var FAR = PHONE ? '200% 0px 200% 0px' : '300% 0px 300% 0px';
  var cardIo = null;

  // The pictures behind the first slide (slides 2 and up) are "deep": kept back until she reaches for them (wake).
  var DEEP_SLIDE = 'slideshow-slide:nth-child(n+2)';

  function isDeep(img) {
    return !!(img.closest && img.closest(DEEP_SLIDE));
  }

  function putAway(img) {
    var src = img.getAttribute('src');
    var srcset = img.getAttribute('srcset');
    if (srcset) { img.setAttribute('data-hb-shed-srcset', srcset); img.removeAttribute('srcset'); }
    if (src) { img.setAttribute('data-hb-shed-src', src); img.removeAttribute('src'); }
  }

  function giveBack(img) {
    var srcset = img.getAttribute('data-hb-shed-srcset');
    var src = img.getAttribute('data-hb-shed-src');
    if (srcset) { img.setAttribute('srcset', srcset); img.removeAttribute('data-hb-shed-srcset'); }
    if (src) { img.setAttribute('src', src); img.removeAttribute('data-hb-shed-src'); }
  }

  /* HB 2026-10-10 (round 28): a far card of the collection grid is not rendered at all. content-visibility: hidden
     skips its style, layout, paint and compositing; the box keeps the height it had (contain-intrinsic-block-size),
     so the grid and the scroll position do not move. Only the grid's cards (the infinite scroll's pages), never the
     cards of a carousel (recently viewed, recommendations), where a card just off the edge would pop in on a swipe. */
  var SKIP_OK = (function () {
    try {
      return !!(window.CSS && CSS.supports && CSS.supports('content-visibility', 'hidden') && CSS.supports('contain-intrinsic-block-size', '1px'));
    } catch (e) { return false; }
  })();
  var GRID_ITEM = '[ref="cards[]"], .product-grid__item';
  var FAR_SCREENS = PHONE ? 2 : 3; // the same margin as FAR, for a card that has just been added (the observer is not there yet)

  function inGrid(card) {
    return !!(card.closest && card.closest(GRID_ITEM));
  }

  // The card is beyond the margin right now (read: forces a layout, so call it in a read phase).
  function farNow(card) {
    var r = card.getBoundingClientRect();
    var vh = window.innerHeight || 1;
    return r.bottom < -FAR_SCREENS * vh || r.top > vh + FAR_SCREENS * vh;
  }

  // Write phase: the card keeps a box of height h and renders nothing.
  function skip(card, h) {
    if (!SKIP_OK || card.__hbSkipped || !inGrid(card)) return;
    if (!(h > 0)) return; // no layout to remember (a hidden container): left as it is
    card.style.containIntrinsicBlockSize = h + 'px';
    card.style.contentVisibility = 'hidden';
    card.__hbSkipped = true;
  }

  function unskip(card) {
    if (!card.__hbSkipped) return;
    card.style.contentVisibility = '';
    card.style.containIntrinsicBlockSize = '';
    card.__hbSkipped = false;
  }

  // Far from the viewport: every picture of the card is put away and, in the grid, the card is not rendered.
  // h is the card's height read in the caller's read phase (so a batch of cards costs one layout, not one each).
  function shed(card, h) {
    if (card.__hbShed) return;
    if (h == null && SKIP_OK && !card.__hbSkipped && inGrid(card)) h = card.offsetHeight;
    var imgs = card.querySelectorAll('img');
    for (var i = 0; i < imgs.length; i++) putAway(imgs[i]);
    card.__hbShed = true;
    skip(card, h);
  }

  // Near the viewport: the card renders again, the first slide, the dots and the quick add's picture come back; the
  // deep slides only once she has reached for them (card.__hbAwake).
  function dress(card) {
    if (!card.__hbShed) return;
    unskip(card);
    var imgs = card.querySelectorAll('img[data-hb-shed-src], img[data-hb-shed-srcset]');
    for (var i = 0; i < imgs.length; i++) {
      if (!card.__hbAwake && isDeep(imgs[i])) continue;
      giveBack(imgs[i]);
    }
    card.__hbShed = false;
  }

  // The widths changed (a turn of the phone, a resized window): the skipped cards are shown for one layout, measured
  // again and skipped again with their new heights.
  var remeasureTimer = 0;
  function remeasure() {
    remeasureTimer = 0;
    var all = document.querySelectorAll(CARD_SEL);
    var list = [];
    var i;
    for (i = 0; i < all.length; i++) {
      if (!all[i].__hbSkipped) continue;
      unskip(all[i]);
      list.push(all[i]);
    }
    if (!list.length) return;
    var hs = [];
    for (i = 0; i < list.length; i++) hs.push(list[i].offsetHeight);
    for (i = 0; i < list.length; i++) skip(list[i], hs[i]);
  }
  if (SKIP_OK) {
    window.addEventListener('resize', function () {
      clearTimeout(remeasureTimer);
      remeasureTimer = setTimeout(remeasure, 250);
    }, { passive: true });
  }

  // From the start (the page's own HTML carries every slide's picture): the deep slides are put away as the card is
  // first seen by this script, before most of them have begun to load.
  function shedDeep(card) {
    if (card.__hbAwake) return;
    var imgs = card.querySelectorAll(DEEP_SLIDE + ' img');
    for (var i = 0; i < imgs.length; i++) putAway(imgs[i]);
  }

  // She reaches for the slides: a touch or a press on the slideshow or its arrows, a swatch, a swipe, a hover.
  function wake(card) {
    if (!card || card.__hbAwake) return;
    card.__hbAwake = true;
    if (card.__hbShed) return; // far away: dress() gives everything back when it comes near
    var imgs = card.querySelectorAll(DEEP_SLIDE + ' img[data-hb-shed-src], ' + DEEP_SLIDE + ' img[data-hb-shed-srcset]');
    for (var i = 0; i < imgs.length; i++) giveBack(imgs[i]);
  }

  function cardOf(target) {
    return target && target.closest ? target.closest(CARD_SEL) : null;
  }

  function onReach(e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (!t.closest('slideshow-component, slideshow-slides, [class*="swatch"]')) return;
    wake(cardOf(t));
  }

  if ('IntersectionObserver' in window) {
    cardIo = new IntersectionObserver(function (entries) {
      // Reads first (the far cards' heights), then the writes, so the batch costs one layout.
      var near = [];
      var far = [];
      var i;
      for (i = 0; i < entries.length; i++) (entries[i].isIntersecting ? near : far).push(entries[i].target);
      var hs = [];
      for (i = 0; i < far.length; i++) hs.push(!far[i].__hbShed && SKIP_OK && !far[i].__hbSkipped && inGrid(far[i]) ? far[i].offsetHeight : 0);
      for (i = 0; i < near.length; i++) dress(near[i]);
      for (i = 0; i < far.length; i++) shed(far[i], hs[i]);
    }, { rootMargin: FAR, threshold: 0 });
    document.addEventListener('touchstart', onReach, { capture: true, passive: true });
    document.addEventListener('pointerdown', onReach, { capture: true, passive: true });
    // A swipe or the arrows scroll the slideshow's own scroller (scroll does not bubble; it is seen in the capture phase).
    document.addEventListener('scroll', function (e) {
      var t = e.target;
      if (t && t.tagName && t.tagName.toLowerCase() === 'slideshow-slides') wake(cardOf(t));
    }, { capture: true, passive: true });
    if (window.matchMedia && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      document.addEventListener('pointerover', function (e) { wake(cardOf(e.target)); }, { capture: true, passive: true });
    }
  }

  function watchCard(card) {
    if (card.__hbCardWatched || !cardIo) return;
    card.__hbCardWatched = true;
    shedDeep(card);
    cardIo.observe(card);
  }

  // The cards under root that this script has not seen yet, each put under the observer.
  function scanCards(root) {
    var list = [];
    if (!cardIo || !root || !root.querySelectorAll) return list;
    if (root.matches && root.matches(CARD_SEL) && !root.__hbCardWatched) list.push(root);
    var found = root.querySelectorAll(CARD_SEL);
    var i;
    for (i = 0; i < found.length; i++) if (!found[i].__hbCardWatched) list.push(found[i]);
    for (i = 0; i < list.length; i++) watchCard(list[i]);
    return list;
  }

  // A page the infinite scroll has just added beyond the margin is skipped at once, before its slideshows get to
  // their layout reads (the observer's first report comes a frame later, after that work has been done). One read
  // phase for the whole batch, then the writes.
  function settleFresh(list) {
    if (!SKIP_OK || !list.length) return;
    var fresh = [];
    var i;
    for (i = 0; i < list.length; i++) {
      if (!list[i].__hbShed && !list[i].__hbSkipped && list[i].isConnected && inGrid(list[i])) fresh.push(list[i]);
    }
    if (!fresh.length) return;
    var far = [];
    var hs = [];
    for (i = 0; i < fresh.length; i++) {
      if (!farNow(fresh[i])) continue;
      far.push(fresh[i]);
      hs.push(fresh[i].offsetHeight);
    }
    for (i = 0; i < far.length; i++) shed(far[i], hs[i]);
  }

  function scanVideos(root) {
    if (!root || !root.querySelectorAll) return;
    if (root.matches && root.matches(SEL)) watch(root);
    var found = root.querySelectorAll(SEL);
    for (var i = 0; i < found.length; i++) watch(found[i]);
  }

  function scan(root) {
    scanVideos(root);
    settleFresh(scanCards(root));
  }

  // Several roots at once (the mutation observer's batch): the cards of all of them share one read phase.
  function scanMany(roots) {
    var cards = [];
    for (var i = 0; i < roots.length; i++) {
      scanVideos(roots[i]);
      cards = cards.concat(scanCards(roots[i]));
    }
    settleFresh(cards);
  }

  // A section re-render (the product form morphs the page on a variant change) rebuilds children from the server's
  // HTML, which has no <source>: a video that had one gets it back.
  function restore(video) {
    if (!video.__hbSrc || !video.isConnected) return;
    if (video.querySelector('source[data-hb]')) return;
    addSource(video, video.__hbSrc);
    if (video.__hbFallback) addSource(video, video.__hbFallback);
    try { video.load(); } catch (e) { /* no-op */ }
    play(video);
  }

  function start() {
    scan(document);
    if (!('MutationObserver' in window)) return;
    var mo = new MutationObserver(function (records) {
      var added = [];
      for (var i = 0; i < records.length; i++) {
        var rec = records[i];
        for (var j = 0; j < rec.addedNodes.length; j++) {
          var node = rec.addedNodes[j];
          if (node.nodeType === 1) added.push(node);
        }
        if (rec.removedNodes.length && rec.target && rec.target.tagName === 'VIDEO' && rec.target.__hbSrc) {
          restore(rec.target);
        }
      }
      if (added.length) scanMany(added);
    });
    mo.observe(document.documentElement, { childList: true, subtree: true });
  }

  // For a test in the console: HBVideo.renditions(video) lists what a video can get, HBVideo.attach(video) gives it.
  window.HBVideo = { version: '2026-10-10c', renditions: renditions, choose: choose, attach: attach, detach: detach, scan: scan, fromPlaylist: fromPlaylist, alive: function () { return alive.slice(); }, wanted: function () { return wanted.slice(); }, reconcile: reconcile, phone: PHONE, shed: shed, dress: dress, wake: wake, skip: skip, unskip: unskip, remeasure: remeasure, skipOk: SKIP_OK };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start);
  else start();
})();
